import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

const EVENT_TYPES = [
  "Engagement",
  "Birthday",
  "Anniversary",
  "Corporate Dinner",
  "Rehearsal Dinner",
  "Full Buyout",
  "Other",
];

export default function PrivateEvents() {
  const { t } = useI18n();
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "Engagement",
    guests: 20,
    preferred_date: today,
    budget: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/private-events`, {
        ...form,
        guests: Number(form.guests),
        budget: form.budget || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success(t("events.success"));
    } catch (err) {
      toast.error("Could not submit", {
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Try again",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="events"
      data-testid="private-events-section"
      className="bg-[#F5F2EA] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono-accent text-[12px] text-[#7A2021] mb-5">
              {t("events.kicker")}
            </p>
            <h2
              data-testid="events-heading"
              className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-[#1A1713]"
            >
              {t("events.heading")}
            </h2>
            <p className="mt-7 text-[#5C5449] max-w-md leading-relaxed">
              {t("events.subtitle")}
            </p>
            <div className="mt-10 aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=80"
                alt="Long table at Ladiosa's"
                className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-1000"
              />
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {done ? (
              <div
                data-testid="events-confirmation"
                className="border border-[#1A1713]/20 p-8 md:p-12 bg-[#FDFBF7]"
              >
                <p className="font-mono-accent text-[12px] text-[#7A2021] mb-4">
                  — Received
                </p>
                <h3 className="font-serif-display text-4xl md:text-5xl mb-5 text-[#1A1713]">
                  {t("events.success")}
                </h3>
                <p className="text-[#5C5449]">
                  Meanwhile, write to{" "}
                  <a href="mailto:events@ladiosas.co" className="brass-link">
                    events@ladiosas.co
                  </a>{" "}
                  for anything urgent.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="events-new-btn"
                  className="mt-8 font-mono-accent text-[13px] border border-[#1A1713]/30 text-[#1A1713] px-6 py-3 hover:border-[#1A1713]"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="events-form"
                className="grid grid-cols-2 gap-x-8 gap-y-7"
              >
                <Field label="Event Type" span="col-span-2 md:col-span-1">
                  <select
                    required
                    data-testid="events-form-type"
                    value={form.event_type}
                    onChange={update("event_type")}
                    className="ldo-input-light"
                  >
                    {EVENT_TYPES.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Guests" span="col-span-2 md:col-span-1">
                  <input
                    type="number"
                    min={2}
                    max={400}
                    required
                    data-testid="events-form-guests"
                    value={form.guests}
                    onChange={update("guests")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Preferred Date" span="col-span-2 md:col-span-1">
                  <input
                    type="date"
                    min={today}
                    required
                    data-testid="events-form-date"
                    value={form.preferred_date}
                    onChange={update("preferred_date")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Budget (optional)" span="col-span-2 md:col-span-1">
                  <input
                    type="text"
                    placeholder="$3,500"
                    data-testid="events-form-budget"
                    value={form.budget}
                    onChange={update("budget")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Full Name" span="col-span-2">
                  <input
                    type="text"
                    required
                    data-testid="events-form-name"
                    value={form.name}
                    onChange={update("name")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Email" span="col-span-2 md:col-span-1">
                  <input
                    type="email"
                    required
                    data-testid="events-form-email"
                    value={form.email}
                    onChange={update("email")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Phone" span="col-span-2 md:col-span-1">
                  <input
                    type="tel"
                    required
                    data-testid="events-form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="ldo-input-light"
                  />
                </Field>
                <Field label="Tell us about the event" span="col-span-2">
                  <textarea
                    rows={4}
                    data-testid="events-form-message"
                    value={form.message}
                    onChange={update("message")}
                    className="ldo-input-light resize-none"
                  />
                </Field>
                <div className="col-span-2">
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="events-form-submit"
                    className="font-mono-accent text-[13px] bg-[#1A1713] text-[#FDFBF7] px-10 py-5 hover:bg-[#7A2021] transition-colors duration-500 disabled:opacity-50"
                  >
                    {busy ? "…" : t("events.submit")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .ldo-input-light {
          width: 100%; background: transparent; color: #1A1713; border: 0;
          border-bottom: 1px solid rgba(26,23,19,0.25); padding: 12px 0 10px;
          font-family: 'DM Sans', sans-serif; font-size: 16px; outline: none;
          transition: border-color .35s ease;
        }
        .ldo-input-light::placeholder { color: rgba(92,84,73,0.6); }
        .ldo-input-light:focus { border-bottom-color: #7A2021; }
      `}</style>
    </section>
  );
}

function Field({ label, span = "col-span-2", children }) {
  return (
    <div className={span}>
      <label className="block font-mono-accent text-[12px] text-[#7A2021] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
